import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { AffiliateFilleulStage, LeadRecord, LeadRequestType, LeadStatus } from "@itaas/pep-contracts";
import { fetchLeads, recordAffiliateConversion, updateLeadStatus } from "./admin-api";

const TYPE_LABELS: Record<LeadRequestType, string> = {
  devis: "Devis",
  demo: "Démo",
  info: "Info licence",
  partner: "Partenaire",
  trial: "Essai gratuit",
  preregister: "Préinscription",
};

const STATUS_OPTIONS: LeadStatus[] = ["new", "contacted", "qualified", "closed"];

const FILLEUL_STAGE_LABEL: Record<AffiliateFilleulStage, string> = {
  lead: "Lead",
  provisioned: "Activé",
  converted: "Converti",
};

function filleulStage(lead: LeadRecord): AffiliateFilleulStage {
  if (lead.affiliateConversionRecordedAt || lead.filleulStage === "converted") return "converted";
  if (lead.provisionedAt || lead.tenantSlug || lead.filleulStage === "provisioned") return "provisioned";
  return "lead";
}

export function AdminLeadsPage() {
  const [searchParams] = useSearchParams();
  const affiliateFromUrl = searchParams.get("affiliate") ?? "";
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [filter, setFilter] = useState<string>(affiliateFromUrl ? "affiliate" : "all");
  const [affiliateFilter, setAffiliateFilter] = useState(affiliateFromUrl);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [convertingId, setConvertingId] = useState<string | null>(null);

  function reload() {
    setLoading(true);
    setError(null);
    const affiliateCode = filter === "affiliate" && affiliateFilter.trim() ? affiliateFilter.trim() : undefined;
    fetchLeads(undefined, affiliateCode)
      .then(setLeads)
      .catch(() => setError("Impossible de charger les demandes."))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
  }, [filter, affiliateFilter]);

  const filtered = useMemo(() => {
    if (filter === "affiliate") {
      return leads.filter((l) => Boolean(l.affiliateCode));
    }
    if (filter === "all") return leads;
    return leads.filter((l) => l.type === filter);
  }, [leads, filter]);

  const counts = useMemo(() => {
    const byType: Record<string, number> = { all: leads.length };
    for (const l of leads) {
      byType[l.type] = (byType[l.type] ?? 0) + 1;
    }
    return byType;
  }, [leads]);

  async function onStatusChange(id: string, status: LeadStatus) {
    const updated = await updateLeadStatus(id, status);
    setLeads((rows) => rows.map((r) => (r.id === id ? updated : r)));
  }

  async function onRecordConversion(id: string) {
    setConvertingId(id);
    setError(null);
    try {
      const updated = await recordAffiliateConversion(id);
      setLeads((rows) => rows.map((r) => (r.id === id ? updated : r)));
    } catch {
      setError("Impossible d'enregistrer la conversion affilié.");
    } finally {
      setConvertingId(null);
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1">Demandes commerciales</h1>
          <p className="text-secondary small mb-0">
            Devis, démos, essais — stockées ici + notification email selon paramètres.
          </p>
        </div>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={reload}>
          Actualiser
        </button>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {(["all", "affiliate", "devis", "demo", "trial", "preregister", "info", "partner"] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`btn btn-sm ${filter === t ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setFilter(t)}
          >
            {t === "all"
              ? "Toutes"
              : t === "affiliate"
                ? `Affiliés (${leads.filter((l) => l.affiliateCode).length})`
                : TYPE_LABELS[t as LeadRequestType]}{" "}
            {t !== "all" && t !== "affiliate" ? `(${counts[t] ?? 0})` : ""}
          </button>
        ))}
      </div>

      {filter === "affiliate" && (
        <div className="mb-3">
          <label className="form-label small">Filtrer par code ambassadeur</label>
          <input
            className="form-control form-control-sm font-monospace"
            style={{ maxWidth: 280 }}
            placeholder="EDU-ABC123"
            value={affiliateFilter}
            onChange={(e) => setAffiliateFilter(e.target.value.toUpperCase())}
          />
        </div>
      )}

      {loading && <p className="text-secondary">Chargement…</p>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="alert alert-light border">Aucune demande pour ce filtre.</div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="table-responsive card border-0 shadow-sm">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Entreprise</th>
                <th>Contact</th>
                <th>Email / Tél.</th>
                <th>Produits</th>
                <th>Tenant</th>
                <th>Affilié</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id}>
                  <td className="small text-nowrap">
                    {new Date(l.createdAt).toLocaleString("fr-FR")}
                  </td>
                  <td>
                    <span className="badge text-bg-primary">{TYPE_LABELS[l.type]}</span>
                  </td>
                  <td>{l.company}</td>
                  <td>{l.name}</td>
                  <td className="small">
                    <a href={`mailto:${l.email}`}>{l.email}</a>
                    {l.phone ? (
                      <>
                        <br />
                        <a href={`tel:${l.phone}`}>{l.phone}</a>
                      </>
                    ) : null}
                  </td>
                  <td className="small">
                    {l.products.join(", ") || "—"}
                    {l.packageSlug ? (
                      <>
                        <br />
                        <span className="text-secondary">
                          pkg: {l.packageSlug}
                          {l.estimatedMonthlyUsd
                            ? ` · ~$${l.estimatedMonthlyUsd}/mois`
                            : ""}
                        </span>
                        {l.platformRoles?.length ? (
                          <>
                            <br />
                            <span className="text-secondary" style={{ fontSize: "0.7rem" }}>
                              rôles: {l.platformRoles.join(", ")}
                              {l.iamRolesByModule
                                ? ` · ${Object.entries(l.iamRolesByModule)
                                    .map(([m, r]) => `${m}:${r.join("+")}`)
                                    .join(" ")}`
                                : ""}
                            </span>
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </td>
                  <td className="small font-monospace">
                    {l.tenantSlug ?? "—"}
                    {l.provisionedAt ? (
                      <div className="text-success" style={{ fontSize: "0.7rem" }}>
                        ✓ provisionné
                      </div>
                    ) : null}
                  </td>
                  <td className="small font-monospace">
                    {l.affiliateCode ?? "—"}
                    {l.affiliateCode ? (
                      <div className="mt-1">
                        <span className="badge text-bg-light border text-dark">
                          {FILLEUL_STAGE_LABEL[filleulStage(l)]}
                        </span>
                      </div>
                    ) : null}
                    {l.affiliateCode && !l.affiliateConversionRecordedAt ? (
                      <div className="mt-1">
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          disabled={convertingId === l.id}
                          onClick={() => void onRecordConversion(l.id)}
                        >
                          {convertingId === l.id ? "…" : "Marquer converti"}
                        </button>
                      </div>
                    ) : null}
                    {l.affiliateConversionRecordedAt ? (
                      <div className="text-success mt-1" style={{ fontSize: "0.7rem" }}>
                        ✓ converti
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={l.status}
                      onChange={(e) => onStatusChange(l.id, e.target.value as LeadStatus)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
